import { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useCompany } from '../../contexts/CompanyContext';
import { useQuery } from '@tanstack/react-query';
import { portalApi } from '../../api/portal';
import { User, Building2, Shield } from 'lucide-react';
import toast from 'react-hot-toast';
import PageHeader from '../../components/PageHeader';
import EmptyState from '../../components/EmptyState';

export default function PortalProfile() {
  const { user, updateProfile } = useAuth();
  const { userCompanies } = useCompany();
  const [name, setName] = useState(user?.name ?? '');
  const [saving, setSaving] = useState(false);

  const { data: myContact } = useQuery({
    queryKey: ['portal-my-contact'],
    queryFn: () => portalApi.getMyContact(),
  });

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    setSaving(true);
    try {
      await updateProfile({ name: name.trim() });
      toast.success('Profile updated');
    } catch (err: any) {
      toast.error(err.message || 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex flex-col gap-4 pb-10">
      <PageHeader
        title="My Profile"
        intro="Your personal account settings and company memberships."
      />

      {/* Profile info */}
      <form onSubmit={handleSave} className="card space-y-4">
        <div className="flex items-center gap-4 mb-2">
          <div className="w-14 h-14 rounded-full brand-gradient flex items-center justify-center text-white text-xl font-bold">
            {(user?.name ?? '?')[0].toUpperCase()}
          </div>
          <div>
            <p className="font-medium text-fg">{user?.name}</p>
            <p className="text-sm text-muted">{user?.email}</p>
          </div>
        </div>

        <div>
          <label className="label">Name</label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="input"
          />
        </div>

        <div>
          <label className="label">Email</label>
          <input type="email" value={user?.email ?? ''} className="input" disabled />
          <p className="text-xs text-muted mt-1">Contact support to change your email</p>
        </div>

        <div className="flex justify-end">
          <button type="submit" disabled={saving} className="btn btn-primary disabled:opacity-50">
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </form>

      {/* Organization info */}
      {myContact?.organization && (
        <div className="card">
          <h2 className="section-title flex items-center gap-2">
            <Building2 className="w-4 h-4" />
            My Organization
          </h2>
          <p className="text-fg font-medium">
            {myContact.organization.name}
          </p>
          <div className="flex items-center gap-2 mt-2 text-sm text-muted">
            <Shield className="w-3.5 h-3.5" />
            {myContact.organization.sharedTicketAccess
              ? 'You can see all tickets from your organization'
              : 'You can see only your own tickets'}
          </div>
        </div>
      )}

      {/* Company memberships */}
      <div className="card">
        <h2 className="section-title flex items-center gap-2">
          <User className="w-4 h-4" />
          Company Memberships
        </h2>
        {userCompanies.length === 0 ? (
          <EmptyState>You are not a member of any company yet.</EmptyState>
        ) : (
          <div className="space-y-2">
            {userCompanies.map((c) => (
              <div
                key={(c as any).id ?? (c as any).uuid}
                className="flex items-center justify-between px-4 py-3 rounded-lg bg-row-odd"
              >
                <span className="text-sm font-medium text-fg">{c.name}</span>
                {c.industry && <span className="text-xs text-muted">{c.industry}</span>}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
